import { Component, Fragment, type ErrorInfo, type ReactNode } from 'react'

import { ErrorView } from './ErrorView.tsx'

import './shell.css'

/**
 * 최상위 에러 경계 — **셸 자체가 던졌을 때**만 여기까지 온다.
 *
 * 화면 하나가 던지면 `RouteErrorScreen` 이 **셸 안에서**, 셸이 던지면 `AppErrorScreen` 이
 * **셸 밖에서** 받는다 — 둘 다 라우트 단(`errorElement`)이다.
 * 여기 오는 것은 **라우터 «밖»**뿐이다: `RouterProvider` 자체 · 구독/캐시 제공자 ·
 * 라우트 표가 아예 못 선 경우. 그때는 라우터 훅도 못 쓴다.
 *
 * 🔴 **백지를 남기지 않는 것이 이 클래스의 전부다.** React 는 경계가 없으면 예외가 난 트리를
 *    통째로 언마운트한다 — 그것이 eric 이 본 흰 화면이다.
 * 🔴 라우터 «바깥»이라 `useNavigate` 를 못 쓴다. 「처음으로」는 주소를 직접 바꿔 다시 받는다 —
 *    라우터가 못 선 상태에서 SPA 이동은 애초에 불가능하다.
 * ⚠ 클래스 컴포넌트인 이유: **렌더 예외를 잡는 훅이 React 에 없다.** `componentDidCatch` ·
 *   `getDerivedStateFromError` 가 유일한 수단이다.
 */
export class AppErrorBoundary extends Component<
  { children: ReactNode },
  { error: unknown; attempt: number }
> {
  override state = { error: null as unknown, attempt: 0 }

  static getDerivedStateFromError(error: unknown) {
    return { error }
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    // 화면에 원문을 안 쓰는 대신 여기 남긴다
    console.error('[앱 렌더 실패]', error, info.componentStack)
  }

  private retry = () => {
    /*
     * 🔴 **`attempt` 를 올려 아래 트리를 통째로 다시 마운트한다.** 오류만 지우면 React 가
     *    살아남은 옛 노드를 재사용해 같은 상태에서 또 던지는 일이 있다.
     */
    this.setState((prev) => ({ error: null, attempt: prev.attempt + 1 }))
  }

  override render() {
    if (this.state.error === null) {
      // ⚠ `Fragment` 다 — 감싸는 `div` 를 하나 만들면 `#root` 의 직계 자식이 바뀐다
      return <Fragment key={this.state.attempt}>{this.props.children}</Fragment>
    }

    return (
      <div className="shell shell--plain">
        <div className="shell__body">
          <ErrorView
            error={this.state.error}
            fatal
            onHome={() => globalThis.location.assign('/')}
            onRetry={this.retry}
          />
        </div>
      </div>
    )
  }
}
